import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { FileText, BookOpen, Wind, Sparkles, Calendar, ChevronRight, MessageSquare, Music, Tag } from 'lucide-react';
import ManageMeditations from './ManageMeditations';
import ManageArticles from './ManageArticles';
import ManageBreathing from './ManageBreathing';
import ManageAffirmations from './ManageAffirmations';
import ManageSessions from './ManageSessions';
import ManageAmbientSounds from './ManageAmbientSounds';
import ManageCoupons from './ManageCoupons';

const CONTENT_SECTIONS = [
  { id: 'meditations', label: 'Meditations', icon: BookOpen, color: 'from-cosmic-purple to-cosmic-pink' },
  { id: 'articles', label: 'Articles', icon: FileText, color: 'from-cosmic-celestial to-cosmic-turquoise' },
  { id: 'breathing', label: 'Breathing Exercises', icon: Wind, color: 'from-cosmic-turquoise to-chakra-heart' },
  { id: 'affirmations', label: 'Affirmations', icon: Sparkles, color: 'from-cosmic-gold to-cosmic-lotus' },
  { id: 'sessions', label: 'Healing Sessions', icon: Calendar, color: 'from-cosmic-pink to-cosmic-purple' },
  { id: 'ambient-sounds', label: 'Ambient Sounds', icon: Music, color: 'from-cyan-500 to-blue-600' },
  { id: 'coupons', label: 'Coupons', icon: Tag, color: 'from-emerald-500 to-teal-600' },
];

export default function AdminContent() {
  const { currentColors } = useTheme();
  const [activeSection, setActiveSection] = useState(null);

  const renderContent = () => {
    switch (activeSection) {
      case 'meditations':
        return <ManageMeditations />;
      case 'articles':
        return <ManageArticles />;
      case 'breathing':
        return <ManageBreathing />;
      case 'affirmations':
        return <ManageAffirmations />;
      case 'sessions':
        return <ManageSessions />;
      case 'ambient-sounds':
        return <ManageAmbientSounds />;
      case 'coupons':
        return <ManageCoupons />;
      default:
        return null;
    }
  };

  if (activeSection) {
    return (
      <div className="space-y-6" data-testid="admin-content-detail">
        <button
          onClick={() => setActiveSection(null)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:opacity-80"
          style={{ color: currentColors.primary }}
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Content Management
        </button>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-content-page">
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-8 h-8" style={{ color: currentColors.primary }} />
          <div>
            <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
              Content Management
            </h1>
            <p style={{ color: currentColors.textMuted }}>
              Manage all platform content
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CONTENT_SECTIONS.map((section) => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className="glass-card p-6 text-left group hover:scale-[1.02] transition-all"
              data-testid={`content-section-${section.id}`}
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${section.color} rounded-xl flex items-center justify-center mb-4 group-hover:shadow-lg transition-all`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h3 
                className="font-cinzel text-lg mb-2 group-hover:opacity-80 transition-colors"
                style={{ color: currentColors.text }}
              >
                {section.label}
              </h3>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>
                Create, edit and manage {section.label.toLowerCase()}
              </p>
              <div className="flex items-center gap-1 mt-4" style={{ color: currentColors.primary }}>
                <span className="text-sm">Manage</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </button>
          );
        })}
        
        {/* Community Moderation Link */}
        <Link
          to="/admin/community"
          className="glass-card p-6 text-left group hover:scale-[1.02] transition-all"
          data-testid="content-section-community"
        >
          <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:shadow-lg transition-all">
            <MessageSquare className="w-7 h-7 text-white" />
          </div>
          <h3 
            className="font-cinzel text-lg mb-2 group-hover:opacity-80 transition-colors"
            style={{ color: currentColors.text }}
          >
            Community Posts
          </h3>
          <p className="text-sm" style={{ color: currentColors.textMuted }}>
            Moderate community posts and comments
          </p>
          <div className="flex items-center gap-1 mt-4" style={{ color: currentColors.primary }}>
            <span className="text-sm">Manage</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </Link>
      </div>
    </div>
  );
}
